import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../api';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button } from '../../components/Button';
import { ScoringRules } from './ScoringRules';
import './Settings.css';
import { API_URL } from '../../api/client';

interface Gateway {
  setting_id: string;
  gateway_name: string;
  is_active: boolean;
  is_test_mode: boolean;
  public_key?: string;
  callback_url?: string;
  webhook_url?: string;
  merchant_email?: string;
  configuration?: any;
  created_at: string;
  updated_at: string;
}

interface NIBSSSettings {
  is_enabled: boolean;
  is_test_mode: boolean;
  api_url: string;
  api_key: string;
  organization_code: string;
  username: string;
  password: string;
}

interface EmailSettings {
  provider: 'sendgrid' | 'ses';
  is_enabled: boolean;
  sendgrid_api_key?: string;
  ses_access_key?: string;
  ses_secret_key?: string;
  ses_region?: string;
  from_email: string;
  from_name: string;
}

interface SMSSettings {
  provider: 'termii' | 'africastalking';
  is_enabled: boolean;
  termii_api_key?: string;
  termii_sender_id?: string;
  africastalking_api_key?: string;
  africastalking_username?: string;
}

type TabType = 'payment-gateways' | 'nibss' | 'profile' | 'communications' | 'scoring-rules';

export const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>(
    (searchParams.get('tab') as TabType) || 'payment-gateways'
  );

  // Payment Gateway states
  const [gateways, setGateways] = useState<Gateway[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedGateway, setSelectedGateway] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);

  const [gatewayFormData, setGatewayFormData] = useState({
    is_active: false,
    is_test_mode: true,
    public_key: '',
    secret_key: '',
    webhook_secret: '',
    callback_url: '',
    webhook_url: '',
    merchant_email: '',
  });

  // NIBSS settings states
  const [nibssSettings, setNibssSettings] = useState<NIBSSSettings>({
    is_enabled: false,
    is_test_mode: true,
    api_url: 'https://api.nibss-plc.com.ng/verifynow',
    api_key: '',
    organization_code: '',
    username: '',
    password: '',
  });
  const [nibssLoading, setNibssLoading] = useState(false);
  const [nibssSaving, setNibssSaving] = useState(false);
  const [nibssTesting, setNibssTesting] = useState(false);

  // Profile/Password change states
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordChanging, setPasswordChanging] = useState(false);

  // Communications settings states
  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    provider: 'sendgrid',
    is_enabled: false,
    sendgrid_api_key: '',
    from_email: '',
    from_name: 'Coin MFI',
  });
  const [smsSettings, setSmsSettings] = useState<SMSSettings>({
    provider: 'termii',
    is_enabled: false,
    termii_api_key: '',
    termii_sender_id: '',
  });
  const [communicationsLoading, setCommunicationsLoading] = useState(false);
  const [communicationsSaving, setCommunicationsSaving] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);
  const [smsTesting, setSmsTesting] = useState(false);

  useEffect(() => {
    if (activeTab === 'payment-gateways') {
      fetchGateways();
    } else if (activeTab === 'nibss') {
      fetchNIBSSSettings();
    } else if (activeTab === 'communications') {
      fetchCommunicationsSettings();
    }
  }, [activeTab]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams({ tab });
    setError('');
    setSuccessMessage('');
  };

  // ===== PAYMENT GATEWAYS FUNCTIONS =====

  const fetchGateways = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getGatewaySettings();

      if (response.success && response.data) {
        setGateways(response.data.gateways);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load gateway settings');
    } finally {
      setLoading(false);
    }
  };

  const handleGatewaySelect = (gateway: Gateway) => {
    setSelectedGateway(gateway.gateway_name);
    setGatewayFormData({
      is_active: gateway.is_active,
      is_test_mode: gateway.is_test_mode,
      public_key: gateway.public_key || '',
      secret_key: '',
      webhook_secret: '',
      callback_url: gateway.callback_url || `${window.location.origin}/payment/callback`,
      webhook_url: gateway.webhook_url || `${window.location.origin.replace('3012', '5030')}/api/webhooks/${gateway.gateway_name}`,
      merchant_email: gateway.merchant_email || '',
    });
    setError('');
    setSuccessMessage('');
  };

  const handleSaveGateway = async () => {
    if (!selectedGateway) return;

    setSaving(true);
    setError('');

    try {
      const payload: any = {
        is_active: gatewayFormData.is_active,
        is_test_mode: gatewayFormData.is_test_mode,
        callback_url: gatewayFormData.callback_url,
        webhook_url: gatewayFormData.webhook_url,
      };

      if (gatewayFormData.public_key.trim()) {
        payload.public_key = gatewayFormData.public_key.trim();
      }

      if (gatewayFormData.secret_key.trim()) {
        payload.secret_key = gatewayFormData.secret_key.trim();
      }

      if (gatewayFormData.webhook_secret.trim()) {
        payload.webhook_secret = gatewayFormData.webhook_secret.trim();
      }

      if (gatewayFormData.merchant_email.trim()) {
        payload.merchant_email = gatewayFormData.merchant_email.trim();
      }

      const response = await adminApi.updateGatewaySettings(selectedGateway, payload);

      if (response.success) {
        setSuccessMessage('Gateway settings updated successfully');
        fetchGateways();

        setGatewayFormData({
          ...gatewayFormData,
          secret_key: '',
          webhook_secret: '',
        });

        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save gateway settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestGateway = async (gatewayName: string) => {
    setTesting(gatewayName);
    setError('');
    setSuccessMessage('');

    try {
      const response = await adminApi.testGatewayConnection(gatewayName);

      if (response.success) {
        setSuccessMessage(`${gatewayName.toUpperCase()} connection test successful!`);
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || `${gatewayName.toUpperCase()} connection test failed`);
    } finally {
      setTesting(null);
    }
  };

  // ===== NIBSS FUNCTIONS =====

  const fetchNIBSSSettings = async () => {
    try {
      setNibssLoading(true);
      const response = await adminApi.getNIBSSSettings();
      if (response.success && response.data) {
        setNibssSettings(response.data.settings);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load NIBSS settings');
    } finally {
      setNibssLoading(false);
    }
  };

  const handleSaveNIBSS = async () => {
    setNibssSaving(true);
    setError('');

    try {
      const response = await adminApi.updateNIBSSSettings(nibssSettings);

      if (response.success) {
        setSuccessMessage('NIBSS settings updated successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save NIBSS settings');
    } finally {
      setNibssSaving(false);
    }
  };

  const handleTestNIBSS = async () => {
    setNibssTesting(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await adminApi.testNIBSSConnection();

      if (response.success) {
        setSuccessMessage('NIBSS VerifyNow API connection test successful!');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'NIBSS connection test failed');
    } finally {
      setNibssTesting(false);
    }
  };

  // ===== PROFILE/PASSWORD FUNCTIONS =====

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordFormData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setPasswordChanging(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: passwordFormData.currentPassword,
          newPassword: passwordFormData.newPassword,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMessage('Password changed successfully!');
        setPasswordFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.message || 'Failed to change password');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
    } finally {
      setPasswordChanging(false);
    }
  };

  // ===== COMMUNICATIONS FUNCTIONS =====

  const fetchCommunicationsSettings = async () => {
    setCommunicationsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/communications-settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (result.success) {
        if (result.data.email) {
          setEmailSettings(result.data.email);
        }
        if (result.data.sms) {
          setSmsSettings(result.data.sms);
        }
      }
    } catch (err: any) {
      console.error('Error fetching communications settings:', err);
    } finally {
      setCommunicationsLoading(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    setCommunicationsSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/communications-settings/email`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(emailSettings),
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMessage('Email settings saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.message || 'Failed to save email settings');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save email settings');
    } finally {
      setCommunicationsSaving(false);
    }
  };

  const handleSaveSMSSettings = async () => {
    setCommunicationsSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/communications-settings/sms`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(smsSettings),
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMessage('SMS settings saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.message || 'Failed to save SMS settings');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save SMS settings');
    } finally {
      setCommunicationsSaving(false);
    }
  };

  const handleTestEmail = async () => {
    const testEmail = prompt('Enter email address to send test email:');

    if (!testEmail || !testEmail.trim()) {
      setError('Please provide a valid email address');
      return;
    }

    setEmailTesting(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/communications-settings/email/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ testEmail: testEmail.trim() }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMessage(`Test email sent successfully to ${testEmail}! Check your inbox.`);
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setError(result.message || 'Failed to send test email');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send test email');
    } finally {
      setEmailTesting(false);
    }
  };

  const handleTestSMS = async () => {
    const phoneNumber = prompt('Enter phone number to send test SMS (with country code, e.g., +234...):');

    if (!phoneNumber || !phoneNumber.trim()) {
      setError('Please provide a valid phone number');
      return;
    }

    setSmsTesting(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/communications-settings/sms/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ phoneNumber: phoneNumber.trim() }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMessage(`Test SMS sent successfully to ${phoneNumber}!`);
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setError(result.message || 'Failed to send test SMS');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send test SMS');
    } finally {
      setSmsTesting(false);
    }
  };

  // ===== UTILITY FUNCTIONS =====

  const getGatewayIcon = (name: string) => {
    switch (name) {
      case 'paystack':
        return '💳';
      case 'flutterwave':
        return '🦋';
      case 'manual':
        return '🏦';
      default:
        return '⚙️';
    }
  };

  const getGatewayDescription = (name: string) => {
    switch (name) {
      case 'paystack':
        return 'Paystack - Accept card payments, bank transfers, USSD, and more';
      case 'flutterwave':
        return 'Flutterwave - Accept payments across multiple channels';
      case 'manual':
        return 'Manual Bank Transfer - Customer submits proof for admin verification';
      default:
        return 'Payment gateway';
    }
  };

  // ===== RENDER FUNCTIONS =====

  const renderPaymentGatewaysTab = () => {
    if (loading) {
      return <LoadingSpinner />;
    }

    return (
      <div className="settings-tab-content">
        <div className="settings-section-header">
          <h2>Payment Gateway Configuration</h2>
          <p>Manage payment gateways for processing customer loan payments</p>
        </div>

        <div className="settings-container">
          <div className="gateways-grid">
            {gateways.map((gateway) => (
              <div
                key={gateway.gateway_name}
                className={`gateway-card ${selectedGateway === gateway.gateway_name ? 'selected' : ''} ${gateway.is_active ? 'active' : ''}`}
                onClick={() => handleGatewaySelect(gateway)}
              >
                <div className="gateway-header">
                  <div className="gateway-title">
                    <span className="gateway-icon">{getGatewayIcon(gateway.gateway_name)}</span>
                    <h3>{gateway.gateway_name.toUpperCase()}</h3>
                  </div>
                  {gateway.is_active && <span className="status-badge active">ACTIVE</span>}
                </div>
                <p className="gateway-description">{getGatewayDescription(gateway.gateway_name)}</p>
                <div className="gateway-meta">
                  <span className={`mode-badge ${gateway.is_test_mode ? 'test' : 'live'}`}>
                    {gateway.is_test_mode ? 'TEST MODE' : 'LIVE MODE'}
                  </span>
                  {gateway.gateway_name !== 'manual' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTestGateway(gateway.gateway_name);
                      }}
                      disabled={testing === gateway.gateway_name}
                      loading={testing === gateway.gateway_name}
                    >
                      Test
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {selectedGateway && (
            <div className="gateway-config">
              <div className="config-header">
                <h3>Configure {selectedGateway.toUpperCase()}</h3>
                <Button
                  onClick={() => setSelectedGateway(null)}
                  variant="secondary"
                  size="sm"
                  disabled={saving}
                >
                  Close
                </Button>
              </div>

              <div className="config-form">
                <div className="form-section">
                  <h4>General Settings</h4>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={gatewayFormData.is_active}
                        onChange={(e) => setGatewayFormData({ ...gatewayFormData, is_active: e.target.checked })}
                      />
                      <span>Set as Active Gateway</span>
                    </label>
                    <small className="form-hint">
                      Only one gateway can be active at a time. Activating this will deactivate others.
                    </small>
                  </div>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={gatewayFormData.is_test_mode}
                        onChange={(e) => setGatewayFormData({ ...gatewayFormData, is_test_mode: e.target.checked })}
                      />
                      <span>Test Mode</span>
                    </label>
                    <small className="form-hint">
                      Use test API keys and sandbox environment. Uncheck for production.
                    </small>
                  </div>
                </div>

                {selectedGateway !== 'manual' && (
                  <div className="form-section">
                    <h4>API Credentials</h4>

                    <div className="form-group">
                      <label htmlFor="public_key">Public Key *</label>
                      <input
                        type="text"
                        id="public_key"
                        value={gatewayFormData.public_key}
                        onChange={(e) => setGatewayFormData({ ...gatewayFormData, public_key: e.target.value })}
                        placeholder={`Enter ${selectedGateway} public key`}
                        disabled={saving}
                      />
                      <small className="form-hint">
                        {gatewayFormData.is_test_mode ? 'Test public key (pk_test_...)' : 'Live public key (pk_live_...)'}
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="secret_key">Secret Key *</label>
                      <input
                        type="password"
                        id="secret_key"
                        value={gatewayFormData.secret_key}
                        onChange={(e) => setGatewayFormData({ ...gatewayFormData, secret_key: e.target.value })}
                        placeholder="Enter secret key (leave blank to keep current)"
                        disabled={saving}
                      />
                      <small className="form-hint">
                        {gatewayFormData.is_test_mode ? 'Test secret key (sk_test_...)' : 'Live secret key (sk_live_...)'}
                        <br />
                        Leave blank to keep the existing secret key.
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="webhook_secret">Webhook Secret</label>
                      <input
                        type="password"
                        id="webhook_secret"
                        value={gatewayFormData.webhook_secret}
                        onChange={(e) => setGatewayFormData({ ...gatewayFormData, webhook_secret: e.target.value })}
                        placeholder="Enter webhook secret (optional)"
                        disabled={saving}
                      />
                      <small className="form-hint">
                        Used to verify webhook signatures. Leave blank to keep current.
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="merchant_email">Merchant Email</label>
                      <input
                        type="email"
                        id="merchant_email"
                        value={gatewayFormData.merchant_email}
                        onChange={(e) => setGatewayFormData({ ...gatewayFormData, merchant_email: e.target.value })}
                        placeholder="merchant@coinmfi.com"
                        disabled={saving}
                      />
                    </div>
                  </div>
                )}

                <div className="form-section">
                  <h4>Integration URLs</h4>

                  <div className="form-group">
                    <label htmlFor="callback_url">Callback URL</label>
                    <input
                      type="text"
                      id="callback_url"
                      value={gatewayFormData.callback_url}
                      onChange={(e) => setGatewayFormData({ ...gatewayFormData, callback_url: e.target.value })}
                      placeholder="https://yourapp.com/payment/callback"
                      disabled={saving}
                    />
                    <small className="form-hint">
                      URL where customers are redirected after payment
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="webhook_url">Webhook URL</label>
                    <input
                      type="text"
                      id="webhook_url"
                      value={gatewayFormData.webhook_url}
                      onChange={(e) => setGatewayFormData({ ...gatewayFormData, webhook_url: e.target.value })}
                      placeholder={`https://yourapi.com/api/webhooks/${selectedGateway}`}
                      disabled={saving}
                      readOnly={selectedGateway === 'manual'}
                    />
                    <small className="form-hint">
                      Configure this URL in your {selectedGateway} dashboard to receive payment notifications
                    </small>
                  </div>
                </div>

                <div className="form-actions">
                  <Button
                    onClick={handleSaveGateway}
                    variant="primary"
                    disabled={saving}
                    loading={saving}
                  >
                    Save Configuration
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="setup-instructions">
          <h3>Setup Instructions</h3>
          <div className="instructions-grid">
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="instruction-icon">🔵</span>
                <h4>Paystack Setup</h4>
              </div>
              <ol>
                <li>Sign up at <a href="https://paystack.com" target="_blank" rel="noopener noreferrer">paystack.com</a></li>
                <li>Get your API keys from Settings → API Keys & Webhooks</li>
                <li>Copy the Public Key and Secret Key</li>
                <li>Configure webhook URL in Paystack dashboard</li>
                <li>Paste all keys above and save</li>
              </ol>
            </div>

            <div className="instruction-card">
              <div className="instruction-header">
                <span className="instruction-icon">🟢</span>
                <h4>Flutterwave Setup</h4>
              </div>
              <ol>
                <li>Sign up at <a href="https://flutterwave.com" target="_blank" rel="noopener noreferrer">flutterwave.com</a></li>
                <li>Get your API keys from Settings → API</li>
                <li>Copy the Public Key and Secret Key</li>
                <li>Configure webhook URL in Flutterwave dashboard</li>
                <li>Paste all keys above and save</li>
              </ol>
            </div>

            <div className="instruction-card">
              <div className="instruction-header">
                <span className="instruction-icon">🏦</span>
                <h4>Manual Gateway</h4>
              </div>
              <p>
                The manual gateway is always available as a fallback. Customers will see your bank account details
                and can upload proof of payment. Admin must verify payments manually.
              </p>
              <p><strong>No API keys required.</strong> Simply activate to use.</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderProfileTab = () => {
    return (
      <div className="settings-tab-content">
        <div className="settings-section-header">
          <h2>👤 Profile & Security</h2>
          <p>Manage your profile settings and change your password</p>
        </div>

        <div className="profile-settings-container">
          <div className="profile-card">
            <div className="config-form">
              <div className="form-section">
                <h4>Change Password</h4>
                <p className="section-description">
                  Update your password to keep your account secure. Make sure your new password is strong and unique.
                </p>

                <form onSubmit={handlePasswordChange}>
                  <div className="form-group">
                    <label htmlFor="currentPassword">Current Password *</label>
                    <input
                      type="password"
                      id="currentPassword"
                      value={passwordFormData.currentPassword}
                      onChange={(e) => setPasswordFormData({ ...passwordFormData, currentPassword: e.target.value })}
                      placeholder="Enter your current password"
                      required
                      disabled={passwordChanging}
                      autoComplete="current-password"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="newPassword">New Password *</label>
                    <input
                      type="password"
                      id="newPassword"
                      value={passwordFormData.newPassword}
                      onChange={(e) => setPasswordFormData({ ...passwordFormData, newPassword: e.target.value })}
                      placeholder="Enter new password (min. 6 characters)"
                      required
                      disabled={passwordChanging}
                      autoComplete="new-password"
                    />
                    <small className="form-hint">
                      Password must be at least 6 characters long
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm New Password *</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={passwordFormData.confirmPassword}
                      onChange={(e) => setPasswordFormData({ ...passwordFormData, confirmPassword: e.target.value })}
                      placeholder="Confirm your new password"
                      required
                      disabled={passwordChanging}
                      autoComplete="new-password"
                    />
                  </div>

                  <div className="form-actions">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={passwordChanging}
                      loading={passwordChanging}
                    >
                      Change Password
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="profile-info-panel">
            <div className="info-section">
              <div className="info-header">
                <span className="info-icon">🔒</span>
                <h4>Password Security Tips</h4>
              </div>
              <ul>
                <li><strong>Use a strong password:</strong> Mix uppercase, lowercase, numbers, and special characters</li>
                <li><strong>Avoid common passwords:</strong> Don't use easily guessable passwords like "123456" or "password"</li>
                <li><strong>Keep it unique:</strong> Don't reuse passwords from other accounts</li>
                <li><strong>Change regularly:</strong> Update your password periodically for better security</li>
                <li><strong>Use a password manager:</strong> Consider using a password manager to generate and store strong passwords</li>
              </ul>
            </div>

            <div className="info-section warning">
              <div className="info-header">
                <span className="info-icon">⚠️</span>
                <h4>Important</h4>
              </div>
              <p>
                After changing your password, you will need to use the new password for all future logins.
                Make sure to remember or securely store your new password.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNIBSSTab = () => {
    if (nibssLoading) {
      return <LoadingSpinner />;
    }

    return (
      <div className="settings-tab-content">
        <div className="settings-section-header">
          <h2>NIBSS VerifyNow API Configuration</h2>
          <p>Configure NIBSS VerifyNow API credentials for BVN verification and other identity services</p>
        </div>

        <div className="nibss-settings-container">
          <div className="nibss-config-card">
            <div className="config-form">
              <div className="form-section">
                <h4>General Settings</h4>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={nibssSettings.is_enabled}
                      onChange={(e) => setNibssSettings({ ...nibssSettings, is_enabled: e.target.checked })}
                    />
                    <span>Enable NIBSS VerifyNow Integration</span>
                  </label>
                  <small className="form-hint">
                    When enabled, BVN verification will use the NIBSS VerifyNow API
                  </small>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={nibssSettings.is_test_mode}
                      onChange={(e) => setNibssSettings({ ...nibssSettings, is_test_mode: e.target.checked })}
                    />
                    <span>Test Mode (Sandbox)</span>
                  </label>
                  <small className="form-hint">
                    Use NIBSS sandbox environment for testing. Uncheck for production.
                  </small>
                </div>
              </div>

              <div className="form-section">
                <h4>API Configuration</h4>

                <div className="form-group">
                  <label htmlFor="nibss_api_url">API Base URL *</label>
                  <input
                    type="text"
                    id="nibss_api_url"
                    value={nibssSettings.api_url || ''}
                    onChange={(e) => setNibssSettings({ ...nibssSettings, api_url: e.target.value })}
                    placeholder="https://api.nibss-plc.com.ng/verifynow"
                    disabled={nibssSaving}
                  />
                  <small className="form-hint">
                    {nibssSettings.is_test_mode
                      ? 'Sandbox URL: https://sandbox.nibss-plc.com.ng/verifynow'
                      : 'Production URL: https://api.nibss-plc.com.ng/verifynow'}
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="nibss_api_key">API Key *</label>
                  <input
                    type="password"
                    id="nibss_api_key"
                    value={nibssSettings.api_key || ''}
                    onChange={(e) => setNibssSettings({ ...nibssSettings, api_key: e.target.value })}
                    placeholder="Enter NIBSS API key (leave blank to keep current)"
                    disabled={nibssSaving}
                  />
                  <small className="form-hint">
                    Your NIBSS VerifyNow API key. Leave blank to keep existing key.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="nibss_org_code">Organization Code *</label>
                  <input
                    type="text"
                    id="nibss_org_code"
                    value={nibssSettings.organization_code || ''}
                    onChange={(e) => setNibssSettings({ ...nibssSettings, organization_code: e.target.value })}
                    placeholder="Enter your organization code"
                    disabled={nibssSaving}
                  />
                  <small className="form-hint">
                    Your organization code provided by NIBSS
                  </small>
                </div>
              </div>

              <div className="form-section">
                <h4>Authentication Credentials</h4>

                <div className="form-group">
                  <label htmlFor="nibss_username">Username *</label>
                  <input
                    type="text"
                    id="nibss_username"
                    value={nibssSettings.username || ''}
                    onChange={(e) => setNibssSettings({ ...nibssSettings, username: e.target.value })}
                    placeholder="Enter NIBSS username"
                    disabled={nibssSaving}
                    autoComplete="off"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="nibss_password">Password *</label>
                  <input
                    type="password"
                    id="nibss_password"
                    value={nibssSettings.password || ''}
                    onChange={(e) => setNibssSettings({ ...nibssSettings, password: e.target.value })}
                    placeholder="Enter NIBSS password (leave blank to keep current)"
                    disabled={nibssSaving}
                    autoComplete="new-password"
                  />
                  <small className="form-hint">
                    Leave blank to keep existing password
                  </small>
                </div>
              </div>

              <div className="form-actions">
                <Button
                  onClick={handleTestNIBSS}
                  variant="secondary"
                  disabled={nibssSaving || nibssTesting || !nibssSettings.is_enabled}
                  loading={nibssTesting}
                >
                  Test Connection
                </Button>
                <Button
                  onClick={handleSaveNIBSS}
                  variant="primary"
                  disabled={nibssSaving || nibssTesting}
                  loading={nibssSaving}
                >
                  Save Configuration
                </Button>
              </div>
            </div>
          </div>

          <div className="nibss-info-panel">
            <div className="info-section">
              <div className="info-header">
                <span className="info-icon">📋</span>
                <h4>Setup Instructions</h4>
              </div>
              <ol>
                <li>
                  <strong>Register with NIBSS:</strong><br />
                  Contact NIBSS to register your organization for VerifyNow API access at{' '}
                  <a href="https://www.nibss-plc.com.ng" target="_blank" rel="noopener noreferrer">
                    nibss-plc.com.ng
                  </a>
                </li>
                <li>
                  <strong>Obtain Credentials:</strong><br />
                  After registration, you'll receive:
                  <ul>
                    <li>API Key</li>
                    <li>Organization Code</li>
                    <li>Username and Password</li>
                    <li>API documentation</li>
                  </ul>
                </li>
                <li>
                  <strong>Configure Sandbox:</strong><br />
                  Use sandbox credentials for testing before going live
                </li>
                <li>
                  <strong>Test Connection:</strong><br />
                  Click "Test Connection" to verify your credentials are working
                </li>
                <li>
                  <strong>Go Live:</strong><br />
                  Switch to production credentials when ready and uncheck "Test Mode"
                </li>
              </ol>
            </div>

            <div className="info-section">
              <div className="info-header">
                <span className="info-icon">ℹ️</span>
                <h4>About NIBSS VerifyNow</h4>
              </div>
              <p>
                NIBSS VerifyNow provides real-time identity verification services including:
              </p>
              <ul>
                <li><strong>BVN Verification:</strong> Verify Bank Verification Numbers</li>
                <li><strong>Identity Matching:</strong> Match names, dates of birth, and phone numbers</li>
                <li><strong>Fraud Detection:</strong> Prevent identity fraud with real-time checks</li>
                <li><strong>Compliance:</strong> Meet KYC and AML requirements</li>
              </ul>
            </div>

            <div className="info-section warning">
              <div className="info-header">
                <span className="info-icon">⚠️</span>
                <h4>Security Notice</h4>
              </div>
              <p>
                <strong>Keep your credentials secure:</strong>
              </p>
              <ul>
                <li>Never share your API key or password</li>
                <li>Use strong, unique passwords</li>
                <li>Regularly rotate credentials</li>
                <li>Monitor API usage for anomalies</li>
                <li>Use test mode for development/testing</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCommunicationsTab = () => {
    if (communicationsLoading) {
      return <LoadingSpinner />;
    }

    return (
      <div className="settings-tab-content">
        <div className="settings-section-header">
          <h2>📧 Communications Settings</h2>
          <p>Configure email and SMS API settings for sending notifications to customers</p>
        </div>

        {/* EMAIL SETTINGS */}
        <div className="communications-settings-container">
          <div className="settings-card">
            <div className="card-header" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: 'white', padding: '1.25rem', borderRadius: '1rem 1rem 0 0' }}>
              <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>📧 Email Settings</h3>
            </div>
            <div className="config-form">
              <div className="form-section">
                <h4>General Settings</h4>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={emailSettings.is_enabled}
                      onChange={(e) => setEmailSettings({ ...emailSettings, is_enabled: e.target.checked })}
                    />
                    <span>Enable Email Notifications</span>
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="emailProvider">Email Provider *</label>
                  <select
                    id="emailProvider"
                    value={emailSettings.provider}
                    onChange={(e) => setEmailSettings({ ...emailSettings, provider: e.target.value as any })}
                    className="form-control"
                  >
                    <option value="sendgrid">SendGrid</option>
                    <option value="ses">Amazon SES</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="fromEmail">From Email Address *</label>
                  <input
                    type="email"
                    id="fromEmail"
                    value={emailSettings.from_email}
                    onChange={(e) => setEmailSettings({ ...emailSettings, from_email: e.target.value })}
                    placeholder="noreply@coinmfi.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="fromName">From Name *</label>
                  <input
                    type="text"
                    id="fromName"
                    value={emailSettings.from_name}
                    onChange={(e) => setEmailSettings({ ...emailSettings, from_name: e.target.value })}
                    placeholder="Coin MFI"
                  />
                </div>
              </div>

              {emailSettings.provider === 'sendgrid' && (
                <div className="form-section">
                  <h4>SendGrid Configuration</h4>

                  <div className="form-group">
                    <label htmlFor="sendgridApiKey">SendGrid API Key *</label>
                    <input
                      type="password"
                      id="sendgridApiKey"
                      value={emailSettings.sendgrid_api_key || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, sendgrid_api_key: e.target.value })}
                      placeholder="SG.xxxxxxxxxxxxx"
                    />
                    <small className="form-hint">
                      Get your API key from <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer">SendGrid Settings</a>
                    </small>
                  </div>
                </div>
              )}

              {emailSettings.provider === 'ses' && (
                <div className="form-section">
                  <h4>Amazon SES Configuration</h4>

                  <div className="form-group">
                    <label htmlFor="sesAccessKey">AWS Access Key ID *</label>
                    <input
                      type="password"
                      id="sesAccessKey"
                      value={emailSettings.ses_access_key || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, ses_access_key: e.target.value })}
                      placeholder="AKIAIOSFODNN7EXAMPLE"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="sesSecretKey">AWS Secret Access Key *</label>
                    <input
                      type="password"
                      id="sesSecretKey"
                      value={emailSettings.ses_secret_key || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, ses_secret_key: e.target.value })}
                      placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="sesRegion">AWS Region *</label>
                    <input
                      type="text"
                      id="sesRegion"
                      value={emailSettings.ses_region || ''}
                      onChange={(e) => setEmailSettings({ ...emailSettings, ses_region: e.target.value })}
                      placeholder="us-east-1"
                    />
                  </div>
                </div>
              )}

              <div className="form-actions">
                <Button
                  onClick={handleSaveEmailSettings}
                  variant="primary"
                  disabled={communicationsSaving}
                  loading={communicationsSaving}
                >
                  Save Email Settings
                </Button>
                <Button
                  onClick={handleTestEmail}
                  variant="secondary"
                  disabled={emailTesting || !emailSettings.is_enabled}
                  loading={emailTesting}
                >
                  Send Test Email
                </Button>
              </div>
            </div>
          </div>

          {/* SMS SETTINGS */}
          <div className="settings-card" style={{ marginTop: '2rem' }}>
            <div className="card-header" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', padding: '1.25rem', borderRadius: '1rem 1rem 0 0' }}>
              <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>📱 SMS Settings</h3>
            </div>
            <div className="config-form">
              <div className="form-section">
                <h4>General Settings</h4>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={smsSettings.is_enabled}
                      onChange={(e) => setSmsSettings({ ...smsSettings, is_enabled: e.target.checked })}
                    />
                    <span>Enable SMS Notifications</span>
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="smsProvider">SMS Provider *</label>
                  <select
                    id="smsProvider"
                    value={smsSettings.provider}
                    onChange={(e) => setSmsSettings({ ...smsSettings, provider: e.target.value as any })}
                    className="form-control"
                  >
                    <option value="termii">Termii (Nigeria)</option>
                    <option value="africastalking">Africa's Talking</option>
                  </select>
                </div>
              </div>

              {smsSettings.provider === 'termii' && (
                <div className="form-section">
                  <h4>Termii Configuration</h4>

                  <div className="form-group">
                    <label htmlFor="termiiApiKey">Termii API Key *</label>
                    <input
                      type="password"
                      id="termiiApiKey"
                      value={smsSettings.termii_api_key || ''}
                      onChange={(e) => setSmsSettings({ ...smsSettings, termii_api_key: e.target.value })}
                      placeholder="TLxxxxxxxxxxxxxxxxxxxxxxxx"
                    />
                    <small className="form-hint">
                      Get your API key from <a href="https://www.termii.com/" target="_blank" rel="noopener noreferrer">Termii Dashboard</a>
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="termiiSenderId">Sender ID *</label>
                    <input
                      type="text"
                      id="termiiSenderId"
                      value={smsSettings.termii_sender_id || ''}
                      onChange={(e) => setSmsSettings({ ...smsSettings, termii_sender_id: e.target.value })}
                      placeholder="CoinMFI"
                    />
                    <small className="form-hint">
                      Must be an approved sender ID (max 11 characters)
                    </small>
                  </div>
                </div>
              )}

              {smsSettings.provider === 'africastalking' && (
                <div className="form-section">
                  <h4>Africa's Talking Configuration</h4>

                  <div className="form-group">
                    <label htmlFor="africastalkingApiKey">API Key *</label>
                    <input
                      type="password"
                      id="africastalkingApiKey"
                      value={smsSettings.africastalking_api_key || ''}
                      onChange={(e) => setSmsSettings({ ...smsSettings, africastalking_api_key: e.target.value })}
                      placeholder="Your Africa's Talking API Key"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="africastalkingUsername">Username *</label>
                    <input
                      type="text"
                      id="africastalkingUsername"
                      value={smsSettings.africastalking_username || ''}
                      onChange={(e) => setSmsSettings({ ...smsSettings, africastalking_username: e.target.value })}
                      placeholder="sandbox or your username"
                    />
                  </div>
                </div>
              )}

              <div className="form-actions">
                <Button
                  onClick={handleSaveSMSSettings}
                  variant="primary"
                  disabled={communicationsSaving}
                  loading={communicationsSaving}
                >
                  Save SMS Settings
                </Button>
                <Button
                  onClick={handleTestSMS}
                  variant="secondary"
                  disabled={smsTesting || !smsSettings.is_enabled}
                  loading={smsTesting}
                >
                  Send Test SMS
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <div className="header-content">
          <h1>⚙️ Settings</h1>
          <p className="header-subtitle">Manage system integrations and configurations</p>
        </div>
        <Button onClick={() => navigate('/admin/dashboard')} variant="secondary">
          Back to Dashboard
        </Button>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {successMessage && (
        <div className="success-message">
          <span>✓ {successMessage}</span>
        </div>
      )}

      <div className="settings-tabs">
        <button
          className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => handleTabChange('profile')}
        >
          <span className="tab-icon">👤</span>
          Profile
        </button>
        <button
          className={`tab-button ${activeTab === 'payment-gateways' ? 'active' : ''}`}
          onClick={() => handleTabChange('payment-gateways')}
        >
          <span className="tab-icon">💳</span>
          Payment Gateways
        </button>
        <button
          className={`tab-button ${activeTab === 'nibss' ? 'active' : ''}`}
          onClick={() => handleTabChange('nibss')}
        >
          <span className="tab-icon">🔐</span>
          NIBSS VerifyNow
        </button>
        <button
          className={`tab-button ${activeTab === 'communications' ? 'active' : ''}`}
          onClick={() => handleTabChange('communications')}
        >
          <span className="tab-icon">📧</span>
          Communications
        </button>
        <button
          className={`tab-button ${activeTab === 'scoring-rules' ? 'active' : ''}`}
          onClick={() => handleTabChange('scoring-rules')}
        >
          <span className="tab-icon">🛡️</span>
          Scoring Rules
        </button>
      </div>

      <div className="settings-content">
        {activeTab === 'profile' && renderProfileTab()}
        {activeTab === 'payment-gateways' && renderPaymentGatewaysTab()}
        {activeTab === 'nibss' && renderNIBSSTab()}
        {activeTab === 'communications' && renderCommunicationsTab()}
        {activeTab === 'scoring-rules' && <ScoringRules />}
      </div>
    </div>
  );
};
